import { useState } from 'react';
import { AxiosError } from 'axios';
import { Check, Copy, Phone, SearchX, ShieldCheck, ShieldAlert } from 'lucide-react';
import type { Member360Tab } from '../../../../application/hooks/team/useTeamUrlState';
import { useTeamMember } from '../../../../application/hooks/team/useTeamQueries';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { formatDate } from '../../../../domain/formatters/date';
import { TIER_DESIGNATION_MAP } from '../../../../domain/enums/TierLevel';
import { QueryState } from '../../../components/feedback/QueryState';
import { Badge } from '../../../components/ui/Badge';
import { STATUS_TONE, initials, isAgent, statusAccent, telHref } from '../teamFormat';
import { ProfileTab } from './ProfileTab';
import { InstallmentsTab } from './InstallmentsTab';
import { TeamTab } from './TeamTab';
import './Member360.css';

export interface Member360PanelProps {
  userId: string;
  tab: Member360Tab;
  onTab: (tab: Member360Tab) => void;
  onOpenMember: (userId: string) => void;
  onFocusMember: (userId: string) => void;
}

/** Selected member's profile + read-only installments + team (PRD 7.5.1 B). */
export function Member360Panel({ userId, tab, onTab, onOpenMember, onFocusMember }: Member360PanelProps) {
  const { t, locale } = useTranslation();
  const member = useTeamMember(userId);
  const [copied, setCopied] = useState(false);

  const is404 =
    (member.error as AxiosError)?.response?.status === 404 ||
    (member.error as { response?: { status?: number } })?.response?.status === 404;

  if (is404) {
    return (
      <div className="tm-empty" role="alert" data-testid="member360-not-found" style={{ padding: '32px 16px', textAlign: 'center' }}>
        <SearchX size={24} aria-hidden style={{ color: '#94a3b8', marginBottom: '8px' }} />
        <p style={{ margin: '0 0 12px 0', fontWeight: 600, color: 'var(--color-text)' }}>{t('team.notFound')}</p>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => onFocusMember('me')}
          data-testid="member360-back-to-team"
        >
          {t('team.backToMyTeam') ?? 'Return to Team Overview'}
        </button>
      </div>
    );
  }
  if (!member.data) {
    return <QueryState loading={member.isPending} error={member.error} onRetry={() => void member.refetch()} skeletonCount={3} />;
  }

  const { card, kycStatus } = member.data;
  const accent = statusAccent(card.status, card.overdueMonths);
  const tabs: Member360Tab[] = isAgent(card.tier) ? ['profile', 'installments', 'team'] : ['profile', 'installments'];
  const activeTab = tabs.includes(tab) ? tab : 'profile';
  const tabLabel = { profile: t('team.tabProfile'), installments: t('team.tabInstallments'), team: t('team.tabTeam') };

  const copyId = () => {
    void navigator.clipboard?.writeText(card.apartmentId).catch(() => undefined);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <article className={`m360 m360--${accent}`} data-testid="member360" aria-label={card.fullName}>
      <header className="m360-head">
        <span className={`tm-avatar tm-avatar--lg tm-avatar--${card.tier.toLowerCase()}`} aria-hidden>
          {initials(card.fullName)}
        </span>
        <div className="m360-id">
          <h2 className="m360-name">{card.fullName}</h2>
          <p className="m360-role">
            {card.tier} · {TIER_DESIGNATION_MAP[card.tier]}
          </p>
          <div className="m360-badges">
            <Badge tone={STATUS_TONE[card.status]}>{t(`team.status_${card.status}`)}</Badge>
            <Badge tone={kycStatus === 'VERIFIED' ? 'success' : 'warning'}>
              {kycStatus === 'VERIFIED' ? <ShieldCheck size={11} aria-hidden /> : <ShieldAlert size={11} aria-hidden />}{' '}
              {kycStatus === 'VERIFIED' ? t('team.kycVerified') : t('team.kycPending')}
            </Badge>
          </div>
          <p className="m360-meta tabular">
            {card.apartmentId} · {t('team.joined')} {formatDate(card.joinedDate, locale)}
          </p>
        </div>
      </header>

      <div className="m360-actions">
        <a className="btn btn-primary btn-sm" href={telHref(card.phone)} data-testid="member360-call">
          <Phone size={14} aria-hidden /> {t('team.call')}
        </a>
        <button type="button" className="btn btn-secondary btn-sm" onClick={copyId}>
          {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />} {copied ? t('team.copied') : t('team.copyId')}
        </button>
      </div>

      <div className="m360-tabs" role="tablist" aria-label={card.fullName}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            className={`m360-tab${activeTab === key ? ' is-active' : ''}`}
            onClick={() => onTab(key)}
            data-testid={`member360-tab-${key}`}
          >
            {tabLabel[key]}
          </button>
        ))}
      </div>

      <div className="m360-panel" role="tabpanel">
        {activeTab === 'profile' && <ProfileTab profile={member.data} onOpenMember={onOpenMember} />}
        {activeTab === 'installments' && <InstallmentsTab userId={userId} profile={member.data} />}
        {activeTab === 'team' && <TeamTab userId={userId} onOpenMember={onOpenMember} onFocusMember={onFocusMember} />}
      </div>
      <p className="m360-footnote">{t('team.readOnly')}</p>
    </article>
  );
}
